import React from 'react';
import { useSelector } from 'react-redux';
import { selectDarkMode } from '../../../app/store/slices/uiSlice';
import {
  CalendarIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  UserGroupIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

// Icon mapping for different stat types
const iconMap = {
  total: CalendarIcon,
  completed: CheckCircleIcon,
  pending: ClockIcon,
  cancelled: XCircleIcon,
  scheduled: CalendarIcon,
  users: UserGroupIcon,
  records: DocumentTextIcon,
  default: DocumentTextIcon
};

// Color schemes for different stat types
const colorSchemes = {
  total: {
    light: 'bg-blue-50 text-blue-600 border-blue-100',
    dark: 'bg-blue-900/20 text-blue-400 border-blue-800/50',
    iconBg: {
      light: 'bg-blue-100',
      dark: 'bg-blue-800/30'
    }
  },
  completed: {
    light: 'bg-green-50 text-green-600 border-green-100',
    dark: 'bg-green-900/20 text-green-400 border-green-800/50',
    iconBg: {
      light: 'bg-green-100',
      dark: 'bg-green-800/30'
    }
  },
  pending: {
    light: 'bg-amber-50 text-amber-600 border-amber-100',
    dark: 'bg-amber-900/20 text-amber-400 border-amber-800/50',
    iconBg: {
      light: 'bg-amber-100',
      dark: 'bg-amber-800/30'
    }
  },
  cancelled: {
    light: 'bg-red-50 text-red-600 border-red-100',
    dark: 'bg-red-900/20 text-red-400 border-red-800/50',
    iconBg: {
      light: 'bg-red-100',
      dark: 'bg-red-800/30'
    }
  },
  scheduled: {
    light: 'bg-purple-50 text-purple-600 border-purple-100',
    dark: 'bg-purple-900/20 text-purple-400 border-purple-800/50',
    iconBg: {
      light: 'bg-purple-100',
      dark: 'bg-purple-800/30'
    }
  },
  default: {
    light: 'bg-gray-50 text-gray-600 border-gray-100',
    dark: 'bg-gray-800/50 text-gray-400 border-gray-700/50',
    iconBg: {
      light: 'bg-gray-100',
      dark: 'bg-gray-700/30'
    }
  }
};

// Format label from key (e.g., "totalAppointments" -> "Total Appointments")
const formatLabel = (key) => {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};

// Single Stat Card Component
const StatCard = ({ label, value, type = 'default', index = 0 }) => {
  const darkMode = useSelector(selectDarkMode);
  const Icon = iconMap[type] || iconMap.default;
  const colors = colorSchemes[type] || colorSchemes.default;

  return (
    <div
      className={`
        relative overflow-hidden rounded-xl border p-4 sm:p-5
        transition-all duration-300 ease-out
        hover:scale-[1.02] hover:shadow-lg
        ${darkMode ? colors.dark : colors.light}
        animate-fadeSlideUp
      `}
      style={{
        animationDelay: `${index * 100}ms`,
        animationFillMode: 'backwards'
      }}
    >
      {/* Background decoration */}
      <div
        className={`
          absolute -right-4 -top-4 h-24 w-24 rounded-full opacity-10
          ${darkMode ? 'bg-white' : 'bg-current'}
        `}
      />

      <div className="relative flex items-center gap-4">
        {/* Icon */}
        <div
          className={`
            flex h-12 w-12 items-center justify-center rounded-xl
            transition-transform duration-300 group-hover:scale-110
            ${darkMode ? colors.iconBg.dark : colors.iconBg.light}
          `}
        >
          <Icon className="h-6 w-6" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p
            className={`
              text-sm font-medium truncate
              ${darkMode ? 'text-gray-400' : 'text-gray-500'}
            `}
          >
            {label}
          </p>
          <p
            className={`
              text-2xl sm:text-3xl font-bold tracking-tight mt-0.5
              transition-all duration-500
              ${darkMode ? 'text-white' : 'text-gray-900'}
            `}
          >
            {typeof value === 'number' ? value.toLocaleString() : value}
          </p>
        </div>
      </div>
    </div>
  );
};

// Main StatsCards Component
const StatsCards = ({ statistics = {}, config = {} }) => {
  const darkMode = useSelector(selectDarkMode);

  // If no statistics, don't render
  if (!statistics || Object.keys(statistics).length === 0) {
    return null;
  }

  // Custom order for stats (if provided in config)
  const statsOrder = config.statsOrder || ['total', 'completed', 'pending', 'cancelled', 'scheduled'];

  // Custom labels (if provided in config)
  const customLabels = config.statsLabels || {};

  // Sort and filter statistics based on order
  const sortedStats = Object.entries(statistics)
    .filter(([key]) => typeof statistics[key] === 'number')
    .sort(([keyA], [keyB]) => {
      const indexA = statsOrder.indexOf(keyA);
      const indexB = statsOrder.indexOf(keyB);
      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });

  return (
    <div className="mb-6">
      <div
        className={`
          grid gap-4
          grid-cols-1 sm:grid-cols-2 lg:grid-cols-${Math.min(sortedStats.length, 4)}
        `}
        style={{
          gridTemplateColumns: `repeat(${Math.min(sortedStats.length, 4)}, minmax(0, 1fr))`
        }}
      >
        {sortedStats.map(([key, value], index) => (
          <StatCard
            key={key}
            label={customLabels[key] || formatLabel(key)}
            value={value}
            type={key}
            index={index}
          />
        ))}
      </div>
    </div>
  );
};

// Loading skeleton for stats
export const StatsCardsSkeleton = ({ count = 3 }) => {
  const darkMode = useSelector(selectDarkMode);

  return (
    <div className="mb-6">
      <div
        className="grid gap-4"
        style={{
          gridTemplateColumns: `repeat(${Math.min(count, 4)}, minmax(0, 1fr))`
        }}
      >
        {Array.from({ length: count }).map((_, index) => (
          <div
            key={index}
            className={`
              rounded-xl border p-4 sm:p-5 animate-pulse
              ${darkMode
                ? 'bg-gray-800/50 border-gray-700/50'
                : 'bg-gray-50 border-gray-100'
              }
            `}
          >
            <div className="flex items-center gap-4">
              <div
                className={`
                  h-12 w-12 rounded-xl
                  ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}
                `}
              />
              <div className="flex-1">
                <div
                  className={`
                    h-4 w-20 rounded mb-2
                    ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}
                  `}
                />
                <div
                  className={`
                    h-8 w-16 rounded
                    ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}
                  `}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StatsCards;
